import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaQuestionCircle } from 'react-icons/fa';

const HelpPage = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <Link to="/" className="inline-flex items-center text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                        <FaArrowLeft className="mr-2" />
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                    {/* Title */}
                    <div className="flex items-center mb-8">
                        <FaQuestionCircle className="text-4xl text-pink-600 mr-4" />
                        <div>
                            <h1 className="text-4xl font-black text-gray-900">Help & FAQ</h1>
                            <p className="text-gray-600 mt-1">Frequently Asked Questions</p>
                        </div>
                    </div>

                    {/* FAQ Sections */}
                    <div className="space-y-10 text-gray-700">
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Account & Registration</h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I create an account?</h3>
                                    <p className="leading-relaxed">Click on "Sign Up" on the homepage, provide your basic details (name, email, phone), set a password, and verify your email or phone number to complete registration.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">I forgot my password. What should I do?</h3>
                                    <p className="leading-relaxed">On the login page, click "Forgot Password?", enter your registered email or phone number, and follow the instructions to reset your password.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I register for someone else (e.g., family member)?</h3>
                                    <p className="leading-relaxed">Yes! You can create and manage a profile for a relative. Just select "Registering for" during signup and provide their details.</p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile & Matching</h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I complete my profile?</h3>
                                    <p className="leading-relaxed">Go to "My Profile" section and fill in details like education, occupation, family background, partner preferences, and upload photos. A complete profile gets more responses!</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How does matching work?</h3>
                                    <p className="leading-relaxed">Our smart algorithm suggests daily matches based on your preferences (age, community, location, education, etc.). You can also search manually using filters.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I hide or delete my profile?</h3>
                                    <p className="leading-relaxed">Yes. Go to Profile Settings → Privacy → choose "Hide Profile" (temporary) or "Delete Account" (permanent). Hidden profiles won't appear in searches.</p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Premium Membership</h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">What are the benefits of Premium?</h3>
                                    <p className="leading-relaxed">Premium members can view contact details, send unlimited messages, see who viewed/liked their profile, and get priority in search results.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I upgrade to Premium?</h3>
                                    <p className="leading-relaxed">Go to "Upgrade" in the menu, choose a plan (1/3/6 months), and complete secure payment.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I cancel auto-renewal?</h3>
                                    <p className="leading-relaxed">Go to Account Settings → Membership → Manage Subscription → Turn off auto-renewal.</p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Safety & Privacy</h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Is my information safe?</h3>
                                    <p className="leading-relaxed">Yes. We use bank-level encryption and never share your contact details without consent. Only verified premium members can view phone/email.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I report a suspicious profile?</h3>
                                    <p className="leading-relaxed">Open the profile → click the three dots → "Report Profile" → select reason. Our team reviews all reports within 24 hours.</p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Payments & Refunds</h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">What payment methods are accepted?</h3>
                                    <p className="leading-relaxed">We accept UPI, credit/debit cards, net banking, and popular wallets.</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I get a refund?</h3>
                                    <p className="leading-relaxed">Yes, within 14 days of purchase if you haven't used premium features. See our <Link to="/refund-policy" className="text-pink-600 underline">Refund Policy</Link> for details.</p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Still Need Help?</h2>
                            <p className="leading-relaxed mb-4">
                                If your question isn't answered here, our support team is happy to help!
                            </p>
                            <div className="p-6 bg-pink-50 rounded-lg">
                                <p className="font-semibold mb-2">Contact Us:</p>
                                <p className="font-semibold">Email: info@softyetech.com</p>
                                <p className="font-semibold">Phone: +91 98765 43210 (9 AM - 9 PM IST)</p>
                                <p className="font-semibold mt-2">We typically respond within 24 hours.</p>
                            </div>
                        </section>
                    </div>

                    {/* Footer */}
                    <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                        <p className="text-gray-600">
                            We're here to help you find your perfect match.
                        </p>
                        <Link to="/" className="inline-block mt-4 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpPage;